if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }

      if (this.variantChangeHandler) {
        this.section?.removeEventListener('on:variant:change', this.variantChangeHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');
      this.variantThreesixtyTrigger = this.querySelector('[data-variant-360-open]');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      this.thumbsLeft = this.classList.contains('media-gallery--thumbs-left');
      this.maxVisibleThumbs = 7;

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      this.updateThumbsOverflow();

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.variantChangeHandler = this.variantChangeHandler || this.onVariantChange.bind(this);
      this.section.addEventListener('on:variant:change', this.variantChangeHandler);
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();
          this.updateThumbsOverflow();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
        this.updateThumbsOverflow();
      }
    }

    /**
     * Limits the left thumbnail column to the first visible thumbs. The last of them
     * gets a "+N" overlay that stands for itself and all hidden thumbs.
     */
    updateThumbsOverflow() {
      if (!this.thumbsLeft || !this.thumbs) return;

      this.moreThumb = null;
      this.thumbsItems.forEach((item) => {
        item.classList.remove('media-thumbs__item--overflow', 'media-thumbs__item--more');
        item.querySelector('.media-thumbs__more')?.remove();
      });

      const visibleThumbs = Array.from(this.thumbsItems)
        .filter((item) => getComputedStyle(item).display !== 'none');
      if (visibleThumbs.length <= this.maxVisibleThumbs) return;

      visibleThumbs.slice(this.maxVisibleThumbs).forEach((item) => {
        item.classList.add('media-thumbs__item--overflow');
      });

      this.moreThumb = visibleThumbs[this.maxVisibleThumbs - 1];
      this.moreThumb.classList.add('media-thumbs__item--more');

      const overlay = document.createElement('span');
      overlay.className = 'media-thumbs__more absolute flex items-center justify-center pointer-events-none';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.textContent = `+${visibleThumbs.length - this.maxVisibleThumbs + 1}`;
      const moreBtn = this.moreThumb.querySelector('.media-thumbs__btn');
      moreBtn.append(overlay);

      // Hand over a server-rendered active state from a hidden thumb.
      const hiddenActiveBtn = this.thumbs.querySelector('.media-thumbs__item--overflow .media-thumbs__btn.is-active');
      if (hiddenActiveBtn) {
        hiddenActiveBtn.classList.remove('is-active');
        hiddenActiveBtn.removeAttribute('aria-current');
        moreBtn.classList.add('is-active');
        moreBtn.setAttribute('aria-current', 'true');
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) {
        this.currentIndex = 0;
        this.currentItem = this.visibleItems[0];
        this.updateVariantThreesixtyTriggerVisibility();
        return;
      }

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
      this.updateVariantThreesixtyTriggerVisibility();
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) {
        this.updateVariantThreesixtyTriggerVisibility();
        return;
      }
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);
      this.updateVariantThreesixtyTriggerVisibility();

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Keeps the variant 360 trigger visible while a 360 viewer is available.
     */
    updateVariantThreesixtyTriggerVisibility() {
      if (!this.variantThreesixtyTrigger || !this.visibleItems) return;

      this.variantThreesixtyTrigger.hidden = false;
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );

      // Media behind the "+N" thumb mark that thumb as active.
      if (this.moreThumb && this.currentThumb.classList.contains('media-thumbs__item--overflow')) {
        this.currentThumb = this.moreThumb;
      }
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}

if (!customElements.get('variant-360-viewer')) {
  class Variant360Viewer extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('.variant-360__modal');
      this.stage = this.querySelector('[data-variant-360-stage]');
      this.frameImage = this.querySelector('[data-variant-360-image]');
      this.frameData = this.querySelector('[data-variant-360-frames]');
      this.loader = this.querySelector('[data-variant-360-loader]');
      this.previousButton = this.querySelector('[data-variant-360-prev]');
      this.nextButton = this.querySelector('[data-variant-360-next]');
      this.playButton = this.querySelector('[data-variant-360-play]');
      this.openButton = document.querySelector(`[data-variant-360-open][aria-controls="${this.modal?.id}"]`);

      if (
        !this.modal
        || !this.stage
        || !this.frameData
        || !this.loader
        || !this.previousButton
        || !this.nextButton
        || !this.playButton
        || !this.openButton
      ) return;

      this.frames = this.getFrames();
      if (this.frames.length < 2) return;

      this.currentFrameIndex = 0;
      this.pendingFrameIndex = null;
      this.initialLoadRequestId = 0;
      this.isInitialLoading = false;
      this.preloadedFrames = new Map();
      this.dragPixelsPerFrame = 16;
      this.autoplayDurationMs = 5000;
      this.autoplayTimer = null;
      this.dragTracking = {
        active: false,
        lastX: 0,
        remainingX: 0,
        pointerId: null
      };

      this.boundOpen = this.open.bind(this);
      this.boundPrevious = this.showPreviousFrame.bind(this);
      this.boundNext = this.showNextFrame.bind(this);
      this.boundToggleAutoplay = this.toggleAutoplay.bind(this);
      this.boundPointerDown = this.handlePointerDown.bind(this);
      this.boundPointerMove = this.handlePointerMove.bind(this);
      this.boundPointerUp = this.handlePointerUp.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
      this.boundVisibilityChange = this.handleVisibilityChange.bind(this);
      this.boundVariantChange = this.handleVariantChange.bind(this);

      this.openButton.addEventListener('click', this.boundOpen);
      this.previousButton.addEventListener('click', this.boundPrevious);
      this.nextButton.addEventListener('click', this.boundNext);
      this.playButton.addEventListener('click', this.boundToggleAutoplay);
      this.stage.addEventListener('pointerdown', this.boundPointerDown);
      this.stage.addEventListener('pointermove', this.boundPointerMove);
      this.stage.addEventListener('pointerup', this.boundPointerUp);
      this.stage.addEventListener('pointercancel', this.boundPointerUp);
      this.stage.addEventListener('keydown', this.boundKeydown);
      document.addEventListener('visibilitychange', this.boundVisibilityChange);
      this.closest('.js-product')?.addEventListener('on:variant:change', this.boundVariantChange);

      this.modalObserver = new MutationObserver(() => {
        if (!this.modal.hasAttribute('open')) {
          this.initialLoadRequestId += 1;
          this.setInitialLoading(false);
          this.stopAutoplay();
        }
      });
      this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    }

    disconnectedCallback() {
      this.initialLoadRequestId += 1;
      this.setInitialLoading(false);
      this.stopAutoplay();
      this.modalObserver?.disconnect();

      this.openButton?.removeEventListener('click', this.boundOpen);
      this.previousButton?.removeEventListener('click', this.boundPrevious);
      this.nextButton?.removeEventListener('click', this.boundNext);
      this.playButton?.removeEventListener('click', this.boundToggleAutoplay);
      this.stage?.removeEventListener('pointerdown', this.boundPointerDown);
      this.stage?.removeEventListener('pointermove', this.boundPointerMove);
      this.stage?.removeEventListener('pointerup', this.boundPointerUp);
      this.stage?.removeEventListener('pointercancel', this.boundPointerUp);
      this.stage?.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.closest('.js-product')?.removeEventListener('on:variant:change', this.boundVariantChange);
    }

    getFrames() {
      try {
        const parsedFrames = JSON.parse(this.frameData.textContent);
        if (Array.isArray(parsedFrames)) {
          return parsedFrames.reduce((validFrames, frame) => {
            if (typeof frame === 'string' && frame.length > 0) {
              validFrames.push({ url: frame, alt: '' });
              return validFrames;
            }

            if (frame && typeof frame === 'object' && typeof frame.url === 'string' && frame.url.length > 0) {
              validFrames.push({
                url: frame.url,
                alt: typeof frame.alt === 'string' ? frame.alt : ''
              });
            }

            return validFrames;
          }, []);
        }
      } catch (error) {
        return [];
      }

      return [];
    }

    open() {
      this.modal.open(this.openButton);
      this.loadInitialFrames();
    }

    loadInitialFrames() {
      const requestId = this.initialLoadRequestId + 1;
      this.initialLoadRequestId = requestId;
      this.stopAutoplay();
      this.setInitialLoading(true);

      const initialFrameIndexes = this.getInitialFrameIndexes();
      Promise.all(initialFrameIndexes.map((frameIndex) => this.loadFrame(frameIndex))).then(() => {
        if (requestId !== this.initialLoadRequestId || !this.modal.hasAttribute('open')) return;

        this.displayFrame(0);
        this.setInitialLoading(false);
        this.stage.focus({ preventScroll: true });
        this.preloadRemainingFrames(initialFrameIndexes);
        this.startAutoplay();
      });
    }

    getInitialFrameIndexes() {
      const minimumFrameCount = Math.max(2, Math.ceil(this.frames.length / 2));
      const frameRadius = Math.ceil((minimumFrameCount - 1) / 2);
      const frameIndexes = [0];

      for (let frameOffset = 1; frameOffset <= frameRadius; frameOffset += 1) {
        frameIndexes.push(this.normalizeFrameIndex(frameOffset));
        frameIndexes.push(this.normalizeFrameIndex(-frameOffset));
      }

      return [...new Set(frameIndexes)];
    }

    preloadRemainingFrames(initialFrameIndexes = []) {
      const initialFrameIndexSet = new Set(initialFrameIndexes);

      this.frames.forEach((frame, frameIndex) => {
        if (!initialFrameIndexSet.has(frameIndex)) {
          this.loadFrame(frameIndex);
        }
      });
    }

    setInitialLoading(isLoading) {
      this.isInitialLoading = isLoading;
      if (!this.loader || !this.stage) return;

      this.loader.hidden = !isLoading;
      this.classList.toggle('variant-360--initial-loading', isLoading);
      this.stage.toggleAttribute('aria-busy', isLoading);

      [this.previousButton, this.nextButton, this.playButton].forEach((button) => {
        if (button) {
          button.disabled = isLoading;
        }
      });
    }

    setFrame(frameIndex) {
      if (this.isInitialLoading) return;

      const normalizedIndex = this.normalizeFrameIndex(frameIndex);
      const frameEntry = this.getFrameEntry(normalizedIndex);
      this.currentFrameIndex = normalizedIndex;

      if (frameEntry?.loaded) {
        this.displayFrame(normalizedIndex);
      } else {
        this.pendingFrameIndex = normalizedIndex;
        this.loadFrame(normalizedIndex).then((loadedFrameEntry) => {
          if (
            this.pendingFrameIndex === normalizedIndex
            && loadedFrameEntry.loaded
            && this.modal.hasAttribute('open')
          ) {
            this.displayFrame(normalizedIndex);
          }
        });
      }

      this.loadFrame(normalizedIndex + 1);
      this.loadFrame(normalizedIndex - 1);
    }

    displayFrame(frameIndex) {
      const normalizedIndex = this.normalizeFrameIndex(frameIndex);
      const frame = this.frames[normalizedIndex];
      const frameUrl = frame.url;
      const frameImage = this.ensureFrameImage();
      frameImage.alt = frame.alt || this.stage.dataset.variant360ImageAlt || '';
      if (frameImage.dataset.currentFrameUrl === frameUrl) return;

      this.currentFrameIndex = normalizedIndex;
      frameImage.dataset.currentFrameUrl = frameUrl;
      frameImage.src = this.getFrameEntry(normalizedIndex)?.image?.src || frameUrl;
    }

    ensureFrameImage() {
      if (this.frameImage) return this.frameImage;

      this.frameImage = document.createElement('img');
      this.frameImage.className = 'variant-360__image';
      this.frameImage.alt = this.stage.dataset.variant360ImageAlt || '';
      this.frameImage.width = 1400;
      this.frameImage.height = 1400;
      this.frameImage.dataset.variant360Image = '';
      this.stage.append(this.frameImage);
      return this.frameImage;
    }

    stepFrame(frameCount) {
      this.setFrame(this.currentFrameIndex + frameCount);
    }

    showPreviousFrame() {
      this.stopAutoplay();
      this.stepFrame(-1);
    }

    showNextFrame() {
      this.stopAutoplay();
      this.stepFrame(1);
    }

    normalizeFrameIndex(frameIndex) {
      return ((frameIndex % this.frames.length) + this.frames.length) % this.frames.length;
    }

    getFrameEntry(frameIndex) {
      const normalizedIndex = this.normalizeFrameIndex(frameIndex);
      const frameUrl = this.frames[normalizedIndex].url;
      return this.preloadedFrames.get(frameUrl);
    }

    loadFrame(frameIndex) {
      const normalizedIndex = this.normalizeFrameIndex(frameIndex);
      const frameUrl = this.frames[normalizedIndex].url;
      const existingFrameEntry = this.preloadedFrames.get(frameUrl);
      if (existingFrameEntry) return existingFrameEntry.promise;

      const preloadedImage = new Image();
      const frameEntry = {
        image: preloadedImage,
        ready: false,
        loaded: false,
        failed: false,
        promise: null
      };

      frameEntry.promise = new Promise((resolve) => {
        preloadedImage.onload = () => {
          const decodePromise = preloadedImage.decode ? preloadedImage.decode().catch(() => {}) : Promise.resolve();

          decodePromise.then(() => {
            frameEntry.ready = true;
            frameEntry.loaded = true;
            resolve(frameEntry);
          });
        };

        preloadedImage.onerror = () => {
          frameEntry.ready = true;
          frameEntry.failed = true;
          resolve(frameEntry);
        };
      });

      preloadedImage.src = frameUrl;
      this.preloadedFrames.set(frameUrl, frameEntry);
      return frameEntry.promise;
    }

    handlePointerDown(event) {
      if (this.isInitialLoading) return;
      if (event.button && event.button !== 0) return;

      this.stopAutoplay();
      this.dragTracking.active = true;
      this.dragTracking.lastX = event.clientX;
      this.dragTracking.remainingX = 0;
      this.dragTracking.pointerId = event.pointerId;
      this.stage.classList.add('is-dragging');
      this.stage.setPointerCapture(event.pointerId);
      event.preventDefault();
    }

    handlePointerMove(event) {
      if (!this.dragTracking.active || event.pointerId !== this.dragTracking.pointerId) return;

      this.dragTracking.remainingX += event.clientX - this.dragTracking.lastX;
      this.dragTracking.lastX = event.clientX;
      const frameDelta = Math.trunc(this.dragTracking.remainingX / this.dragPixelsPerFrame);

      if (frameDelta !== 0) {
        this.dragTracking.remainingX -= frameDelta * this.dragPixelsPerFrame;
        this.stepFrame(frameDelta);
      }

      event.preventDefault();
    }

    handlePointerUp(event) {
      if (!this.dragTracking.active || event.pointerId !== this.dragTracking.pointerId) return;

      this.dragTracking.active = false;
      this.dragTracking.pointerId = null;
      this.stage.classList.remove('is-dragging');

      if (this.stage.hasPointerCapture(event.pointerId)) {
        this.stage.releasePointerCapture(event.pointerId);
      }
    }

    handleKeydown(event) {
      if (this.isInitialLoading) return;

      if (event.key === 'ArrowLeft') {
        this.stopAutoplay();
        this.stepFrame(-1);
        event.preventDefault();
      }

      if (event.key === 'ArrowRight') {
        this.stopAutoplay();
        this.stepFrame(1);
        event.preventDefault();
      }

      if (event.key === ' ') {
        this.toggleAutoplay();
        event.preventDefault();
      }
    }

    toggleAutoplay() {
      if (this.autoplayTimer) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (this.isInitialLoading) return;

      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(
        () => this.stepFrame(1),
        this.autoplayDurationMs / this.frames.length
      );
      this.playButton.classList.add('is-playing');
      this.playButton.setAttribute('aria-label', this.playButton.dataset.pauseLabel);
      this.playButton.setAttribute('aria-pressed', 'true');
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      this.playButton?.classList.remove('is-playing');
      this.playButton?.setAttribute('aria-pressed', 'false');
      if (this.playButton?.dataset.playLabel) {
        this.playButton.setAttribute('aria-label', this.playButton.dataset.playLabel);
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      }
    }

    handleVariantChange() {
      this.initialLoadRequestId += 1;
      this.setInitialLoading(false);
      this.stopAutoplay();
    }
  }

  customElements.define('variant-360-viewer', Variant360Viewer);
}
